// context/DashboardContext.jsx
import React, { createContext, useContext, useState } from "react";
import { fetchAppointments } from "../utils/patients/dashboard/fetchAppointment";
import { fetchTransactions } from "../utils/patients/dashboard/fechTransactions";
import { CancelAppointment } from "../utils/patients/cancelAppointment";
import { reScheduleAppointment } from "../utils/patients/reScheduleAppointment";
import { toast } from "sonner";
import useProfileStore from "../store/patient/profileStore";
import { useAuth } from "../context/AuthContext";
import { fetchVitals } from "../utils/patients/fetchVitals";
import useManagePatient from "../hooks/patientDashboard/useManagePatient";
import { fetchUpcommingAppointments } from "../utils/patients/dashboard/fetchUpcommingAppointment";
import { FetchDoctorsWithAppointment } from "../utils/patients/dashboard/fetchDoctorsWithAppointments";

const DashboardContext = createContext();

export const DashboardProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [vitals, setVitals] = useState(null);
  const [appointments, setAppointment] = useState([]);
  const [transactions, setTransanctions] = useState([]);
  const [confirmModal, setConfirmModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [upcommingAppointments, setUpcommingAppointments] = useState([]);
  const [doctorsWithAppointments, setDoctorsWithAppointments] = useState([]);

  const { profile } = useProfileStore();
  const { findPatient } = useManagePatient();

  const { user } = useAuth();

  const redirectToLogin = (err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      localStorage.removeItem("tokenV2");
      localStorage.removeItem("userId");
      window.location.href = "/pages/signin2";
    }
  };

  const handleFetchVitals = async () => {
    const patientId = user?.id || profile?.id;
    if (!patientId) return;
    setLoading(true);
    try {
      const res = await fetchVitals(patientId);
      setVitals(res?.data);
    } catch (err) {
      redirectToLogin(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFetAppointments = async () => {
    const patientId = profile?.id || user?.id;
    if (!patientId) return;
    setLoading(true);
    try {
      const res = await fetchAppointments(patientId);
      setAppointment(res?.data?.result);
    } catch (err) {
      redirectToLogin(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchUpcommingAppointments = async (type) => {
    if (!profile?.id) return;
    setLoading(true);
    try {
      const res = await fetchUpcommingAppointments(profile?.id, type);
      setUpcommingAppointments(res?.data?.upcomingAppointments);
    } catch (err) {
      redirectToLogin(err);
    } finally {
      setLoading(false);
    }
  };

   const handleFetchDoctorsWithAppointment = async (type) => {
    setLoading(true);
    try {
      const res = await FetchDoctorsWithAppointment("2025-12");
      setDoctorsWithAppointments(res);
    } catch (err) {
      // ignore
    } finally {
      setLoading(false);
    }
  };
// /hr/housekeeping/rosters?period=2025-12&department_id=

  const handleFetchTransactions = async () => {
    const patientId = user?.id || profile?.id;
    if (!patientId) return;
    setLoading(true);
    try {
      const res = await fetchTransactions(patientId);
      setTransanctions(res?.data?.result);
    } catch (err) {
      redirectToLogin(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAppointment = async (data) => {
    setLoading(true);
    try {
      await CancelAppointment(data);
      toast.success("Appointment Cancelled Successfully");
      setConfirmModal(false);
      handleFetAppointments();
      findPatient();
    } catch (err) {
      redirectToLogin(err);
    } finally {
      setLoading(false);
    }
  };

  const handleReScheculeAppointment = async (data) => {
    setLoading(true);
    try {
      await reScheduleAppointment(data);
      toast.success("Appointment Scheduled Successfully!");
      handleFetAppointments();
      setShowDetailsModal(false);
    } catch (err) {
      toast.error("Failed to schedule appointment.");
      redirectToLogin();
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardContext.Provider
      value={{
        vitals,
        appointments,
        transactions,
        loading,
        confirmModal,
        showDetailsModal,
        handleFetchVitals,
        handleFetAppointments,
        handleFetchTransactions,
        handleCancelAppointment,
        handleReScheculeAppointment,
        setConfirmModal,
        setShowDetailsModal,
        handleFetchUpcommingAppointments,
        upcommingAppointments,
        doctorsWithAppointments,
        setDoctorsWithAppointments,
        handleFetchDoctorsWithAppointment,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error("useDashboard must be used within a DashboardProvider");
  }
  return context;
};
